import {RefObject, useEffect, useRef, useState} from 'react';

import {useSize} from '../../../../../libs/shared/ui/src/hooks/sizeHooks';

/**
 * When having the first column sticky, this hook detects if there is an overflow on the Table and if the user has reached the end of the horizontal scroll.
 * @param {boolean} isFirstColumnSticky  - If the user doesn't want a sticky first column, this hook does nothing.
 * @returns {RefObject<HTMLDivElement>} tableContainerRef - useRef to be applied to the table container
 * @returns {RefObject<HTMLTableElement>} tableRef - useRef to be applied to the table container
 * @returns {boolean} hasOverflow - true if there is an overflow on the table (user needs to scroll)
 * @returns {boolean} hasScrolled - true if the user has scrolled.
 * @returns {boolean} hasReachedScrollEnd - true if the user has reached the end of the horizontal scroll
 */
// @TODO support multi column stickyness https://frontjira.atlassian.net/browse/PB-27199
export function useScrollableTable(
  isFirstColumnSticky?: boolean,
): [RefObject<HTMLDivElement>, RefObject<HTMLTableElement>, boolean, boolean, boolean] {
  // Table container element.
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const containerSizeInfo = useSize(tableContainerRef);
  // Table element.
  const tableRef = useRef<HTMLTableElement>(null);
  const tableSizeInfo = useSize(tableRef);

  // hasScrolled & hasReachedScrollEnd to toggle the shadow display on the right and left sides.
  const [hasScrolled, setHasScrolled] = useState(false);
  const [hasReachedScrollEnd, setHasReachedScrollEnd] = useState(false);
  // hasOverflow to toggle the scroll effect and shadow on first column.
  const [hasOverflow, setHasOverflow] = useState(false);

  // setHasReachedScrollEnd.
  useEffect(() => {
    const tableContainer = tableContainerRef?.current;

    if (!isFirstColumnSticky || !tableContainer || !tableSizeInfo || !containerSizeInfo) {
      return () => {};
    } // Don't do anything if the first column isn't sticky or refs aren't set.

    const onScroll = () => {
      // If the table container width + its scrollLeft value is equal to the table total width, that means we reached the horizontal end.
      // Checking using Math.floor & Maths.abs to avoid calculation innacuracies coming from some browsers (Safari / Firefox 👀)
      setHasReachedScrollEnd(
        Math.floor(Math.abs(tableSizeInfo.width - (containerSizeInfo.width + tableContainer.scrollLeft))) ===
          0,
      );
      setHasScrolled(tableContainer.scrollLeft !== 0);
    };
    tableContainer.addEventListener('scroll', onScroll);

    return () => tableContainer.removeEventListener('scroll', onScroll);
  }, [containerSizeInfo, tableSizeInfo, isFirstColumnSticky]);

  // setHasOverflow.
  useEffect(() => {
    if (!isFirstColumnSticky || !tableSizeInfo || !containerSizeInfo) {
      return;
    } // Don't do anything if the first column isn't sticky or refs aren't set.

    setHasOverflow(tableSizeInfo.width > containerSizeInfo.width); // There is an overflow if the Table width is superior to the Table container width.
  }, [containerSizeInfo, isFirstColumnSticky, tableSizeInfo]);

  return [tableContainerRef, tableRef, hasOverflow, hasScrolled, hasReachedScrollEnd];
}
